import type { FC, ReactNode } from 'react'

import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    id?: string
    eyebrow?: string
    title: string
    subtitle?: string
    tone?: 'light' | 'dark'
    className?: string
}

const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    eyebrow,
    title,
    subtitle,
    tone = 'light',
    className
}): ReactNode => {
    const dark = tone === 'dark'

    return (
        <div className={cn('mx-auto max-w-2xl text-center', className)}>
            {eyebrow && (
                <p className={cn('inline-flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.3em] sm:text-sm', dark ? 'text-rose-300' : 'text-primary')}>
                    <span aria-hidden className='h-px w-8 bg-current opacity-50' />
                    {eyebrow}
                    <span aria-hidden className='h-px w-8 bg-current opacity-50' />
                </p>
            )}

            <h2
                id={id}
                className={cn(
                    'font-heading text-4xl font-semibold leading-tight tracking-tight text-balance sm:text-5xl',
                    eyebrow && 'mt-4',
                    dark ? 'text-white' : 'text-foreground'
                )}
            >
                {title}
            </h2>

            <div aria-hidden className={cn('mt-5 flex items-center justify-center gap-3', dark ? 'text-rose-300' : 'text-primary')}>
                <span className='h-px w-12 bg-current opacity-30' />
                <Heart className='size-4 fill-current' />
                <span className='h-px w-12 bg-current opacity-30' />
            </div>

            {subtitle && (
                <p className={cn('mt-5 text-base leading-relaxed text-pretty sm:text-lg', dark ? 'text-white/75' : 'text-muted-foreground')}>
                    {subtitle}
                </p>
            )}
        </div>
    )
}

export default SectionHeading
